import { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { Card, Space, Typography, Button, Modal, Form, Input, App } from "antd";

import { formatRMFixed2 } from "../../../utils/numberFormat";
import { updateFileProfit } from "../api/fileAPI";
import {
  IFileResponse,
  IFileProfitRequest,
  FileProfitRequestSchema,
} from "../type/file";

const { Text } = Typography;

const DetailSection = ({
  title,
  content,
}: {
  title: string;
  content: string;
}) => (
  <div style={{ marginBottom: "16px" }}>
    <Text strong>{title}:</Text>
    <div style={{ marginTop: "4px" }}>
      <Text>{content}</Text>
    </div>
  </div>
);

interface FileProfitProps {
  fileData: IFileResponse;
  refetchFile: () => void;
}

export const FileProfit = ({ fileData, refetchFile }: FileProfitProps) => {
  const { message } = App.useApp();
  const [isUpdateProfitModalOpen, setIsUpdateProfitModalOpen] = useState(false);
  const [confirmUpdateProfitLoading, setConfirmUpdateProfitLoading] =
    useState(false);
  const formRef = useRef<any>(null);

  /* ================ Update Profit ================ */
  const showProfitUpdateModal = () => {
    setIsUpdateProfitModalOpen(true);
    // Set form values to existing file profit data (default to 0 if not available)
    reset({
      fileRevenue: fileData.fileProfit?.fileRevenue ?? 0,
      disbursement: fileData.fileProfit?.disbursement ?? 0,
      agentCommission: fileData.fileProfit?.agentCommission ?? 0,
      downpayment: fileData.fileProfit?.downpayment ?? 0,
    });
  };

  const {
    control,
    handleSubmit: handleSubmitProfit,
    reset,
    formState: { errors },
  } = useForm<IFileProfitRequest>({
    resolver: zodResolver(FileProfitRequestSchema),
    defaultValues: {
      fileRevenue: fileData.fileProfit?.fileRevenue ?? 0,
      disbursement: fileData.fileProfit?.disbursement ?? 0,
      agentCommission: fileData.fileProfit?.agentCommission ?? 0,
      downpayment: fileData.fileProfit?.downpayment ?? 0,
    },
  });

  const onSubmitProfit = async (data: IFileProfitRequest) => {
    if (!fileData) return;

    try {
      setConfirmUpdateProfitLoading(true);

      // Use the updated updateFileProfit function with the file parameter
      await updateFileProfit(fileData.id, data);

      // Refresh the file data using refetch
      await refetchFile();

      message.success("File profit updated successfully");
      setIsUpdateProfitModalOpen(false);
    } catch (error: any) {
      const errorMessage = error.message || "Failed to update file profit";
      message.error(errorMessage);
    } finally {
      setConfirmUpdateProfitLoading(false);
    }
  };

  const handleProfitCancel = () => {
    setIsUpdateProfitModalOpen(false);
    // Reset to original values from file data
    reset({
      fileRevenue: fileData.fileProfit?.fileRevenue ?? 0,
      disbursement: fileData.fileProfit?.disbursement ?? 0,
      agentCommission: fileData.fileProfit?.agentCommission ?? 0,
      downpayment: fileData.fileProfit?.downpayment ?? 0,
    });
  };
  /* ================ End of Update Profit ================ */

  return (
    <>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Card title="Profit" type="inner">
          <DetailSection
            title="File Revenue"
            content={formatRMFixed2(fileData.fileProfit?.fileRevenue)}
          />
          <DetailSection
            title="Disbursement"
            content={formatRMFixed2(fileData.fileProfit?.disbursement)}
          />
          <DetailSection
            title="Agent Commission"
            content={formatRMFixed2(fileData.fileProfit?.agentCommission)}
          />
          <DetailSection
            title="Downpayment"
            content={formatRMFixed2(fileData.fileProfit?.downpayment)}
          />
          <DetailSection
            title="Net Profit"
            content={formatRMFixed2(fileData.fileProfit?.netProfit)}
          />
        </Card>
        <Button
          type="primary"
          style={{
            display: "block",
            marginLeft: "auto",
          }}
          onClick={showProfitUpdateModal}
        >
          Edit File Profit
        </Button>
      </Space>

      <Modal
        title="Update File Profit"
        open={isUpdateProfitModalOpen}
        onOk={handleSubmitProfit(onSubmitProfit)}
        onCancel={handleProfitCancel}
        confirmLoading={confirmUpdateProfitLoading}
      >
        <Form
          ref={formRef}
          layout="vertical"
          onFinish={handleSubmitProfit(onSubmitProfit)}
        >
          <Form.Item
            label="File Revenue"
            name="fileRevenue"
            hasFeedback
            validateStatus={errors.fileRevenue ? "error" : ""}
            help={errors.fileRevenue?.message}
          >
            <Controller
              name="fileRevenue"
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <Input
                  type="number"
                  placeholder="Enter file revenue"
                  {...field}
                  value={value ?? 0}
                  onChange={(e) => {
                    const val = e.target.value;
                    onChange(val === "" ? 0 : parseFloat(val));
                  }}
                  size="large"
                />
              )}
            />
          </Form.Item>
          <Form.Item
            label="Disbursement"
            name="disbursement"
            hasFeedback
            validateStatus={errors.disbursement ? "error" : ""}
            help={errors.disbursement?.message}
          >
            <Controller
              name="disbursement"
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <Input
                  type="number"
                  placeholder="Enter disbursement"
                  {...field}
                  value={value ?? 0}
                  onChange={(e) => {
                    const val = e.target.value;
                    onChange(val === "" ? 0 : parseFloat(val));
                  }}
                  size="large"
                />
              )}
            />
          </Form.Item>
          <Form.Item
            label="Agent Commission"
            name="agentCommission"
            hasFeedback
            validateStatus={errors.agentCommission ? "error" : ""}
            help={errors.agentCommission?.message}
          >
            <Controller
              name="agentCommission"
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <Input
                  type="number"
                  placeholder="Enter agent commission"
                  {...field}
                  value={value ?? 0}
                  onChange={(e) => {
                    const val = e.target.value;
                    onChange(val === "" ? 0 : parseFloat(val));
                  }}
                  size="large"
                />
              )}
            />
          </Form.Item>
          <Form.Item
            label="Downpayment"
            name="downpayment"
            hasFeedback
            validateStatus={errors.downpayment ? "error" : ""}
            help={errors.downpayment?.message}
          >
            <Controller
              name="downpayment"
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <Input
                  type="number"
                  placeholder="Enter downpayment"
                  {...field}
                  value={value ?? 0}
                  onChange={(e) => {
                    const val = e.target.value;
                    onChange(val === "" ? 0 : parseFloat(val));
                  }}
                  size="large"
                />
              )}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
